import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { LinkTypeEnum } from '@hqo/hqo-miniapp-client-sdk';
import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { useQuery } from '@tanstack/react-query';
import { useApi } from 'app/shared/contexts/api';
import { useOpenParams } from 'app/shared/hooks/use-open-params';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';

/** A service provider in the v3 providers list. The name lets us target the owning provider directly —
 *  the live wire carries a top-level `name`, but we also read the nested/interface variants defensively. */
interface ProviderLite {
  uuid?: string;
  name?: string;
  service_provider?: { name?: string };
  client_name?: string;
}

/** The provider's display name across the shapes it appears in (top-level on the wire; nested per the
 *  TS interface). Matched against the transaction's `service_provider_name`. */
const providerDisplayName = (p: ProviderLite): string | undefined => p.name ?? p.service_provider?.name ?? p.client_name;
interface ProvidersResponseBody {
  data: { service_providers?: ProviderLite[] };
}

/** A service in a provider's v3 services list. `content_uuid` points at the event's content shell. */
interface ServiceLite {
  id?: string | number;
  uuid?: string;
  content_uuid?: string;
}
interface ServicesResponseBody {
  data: { services?: ServiceLite[] };
}

/** The event content shell carries the canonical deeplink under `navigation_command`. */
interface ContentResponseBody {
  data: {
    content?: {
      navigation_command?: { type?: string; options?: { url?: string } };
    };
  };
}

interface EventServiceHit {
  companyUuid: string;
  serviceUuid: string;
  contentUuid?: string;
}

interface UseEventDeeplink {
  /** The resolved event-details url, or `null` until/unless it resolves (drives CTA visibility). */
  url: string | null;
  /** Open the resolved url as a webview (no-op until resolved). */
  open: () => void;
}

/** Owner scope for the v3 service-booking endpoints (the Activity Center is always building-scoped). */
const OWNER_TYPE = 'BUILDING';

/**
 * Resolve an event's "View event" deeplink.
 *
 * NOTE (VIS-1541): this is NOT on the feed path and the search projection cannot replace it. Its only
 * caller is the booking-detail page, which reads `details.service_id` off the TRANSACTIONS endpoint —
 * a numeric id, with no uuids anywhere on that payload. The feed's `open_record` routing details
 * (`eventUuid`, plus `companyUuid`/`timeSlotUuid` when present) never reach here. Two further reasons
 * the scan stays: legacy pre-#303 documents carry `eventUuid` alone, so even a feed-fed caller would
 * need it to reach a provider; and only the scan yields the service's `content_uuid`, which is what
 * makes the canonical `navigation_command` lookup below possible (that lookup is what routes a building
 * to the correct events miniapp, legacy vs SBS).
 *
 * The transaction carries only a numeric `service_id` — no UUIDs. So we resolve via the **v3**
 * service-booking endpoints (v3 is the events path; v2 isn't configured for newer-events buildings):
 * list the building's providers, then match the numeric id → the service's `uuid` (serviceUuid) + its
 * provider's `uuid` (companyUuid).
 *
 * The per-provider services endpoints are heavy, so we avoid scanning all of them: the transaction
 * names its provider (`providerName`), which we match against the providers list to fetch **just that
 * one** provider's services. We only fall back to scanning the rest if that targeted lookup misses.
 *
 * For the url we prefer the content shell's `navigation_command` (fetched via the service's
 * `content_uuid`) — the canonical, app-wide deeplink content-service hands out, which also routes to the
 * correct events miniapp (legacy vs SBS) per building. Falls back to a built building-prefixed route.
 *
 * v3 lists only *currently-available* services, so a past event won't resolve — that's **intended**:
 * past events archive themselves and shouldn't be reopenable. We resolve up-front (react-query) and
 * expose `url = null` until it succeeds, so the caller simply hides the CTA (never a dead-tap).
 */
export const useEventDeeplink = (serviceId?: string, providerName?: string): UseEventDeeplink => {
  const { apiUrl, getAuthHeaders, getDefaultHeaders } = useApi();
  const { buildingUuid } = useOpenParams();
  const sdk = useMiniappSdk();
  const { locale } = useIntl();

  const sbBaseUrl = process.env.REACT_APP_SERVICE_BOOKING_URL;
  const enabled = !!serviceId && !!sbBaseUrl && !!buildingUuid && !!apiUrl;

  const resolveEventUrl = async (): Promise<string | null> => {
    const headers = { ...(await getAuthHeaders()), ...getDefaultHeaders(buildingUuid) };
    const providersBase = `${apiUrl}/api/service-booking/v3/${OWNER_TYPE}/${encodeURIComponent(buildingUuid)}/providers`;

    // Find the numeric id in one provider's services; returns the hit or null (isolated per provider so
    // a single flaky adapter — e.g. a 502 mock — can't sink the resolve).
    const findInProvider = async (provider: ProviderLite): Promise<EventServiceHit | null> => {
      if (!provider.uuid) return null;
      try {
        const servicesPayload = await apiRequest<ServicesResponseBody, void, void>({
          url: `${providersBase}/${encodeURIComponent(provider.uuid)}/services`,
          method: HttpMethod.GET,
          headers,
        });
        const services = !servicesPayload.isEmpty() ? servicesPayload.data.data?.services ?? [] : [];
        const match = services.find((s) => s.id != null && String(s.id) === serviceId);
        return match?.uuid ? { companyUuid: provider.uuid, serviceUuid: match.uuid, contentUuid: match.content_uuid } : null;
      } catch {
        return null;
      }
    };

    const providersPayload = await apiRequest<ProvidersResponseBody, void, void>({
      url: providersBase,
      method: HttpMethod.GET,
      headers,
    });
    const providers = !providersPayload.isEmpty() ? providersPayload.data.data?.service_providers ?? [] : [];

    // Fast path: the transaction names its provider → check just that one.
    const named = providerName
      ? providers.find((p) => !!p.uuid && providerDisplayName(p) === providerName)
      : undefined;
    let hit = named ? await findInProvider(named) : null;

    // Fallback: scan the remaining providers only if the targeted lookup missed (or there was no match).
    if (!hit) {
      const rest = providers.filter((p) => p.uuid && p !== named);
      const hits = await Promise.all(rest.map(findInProvider));
      hit = hits.find((h): h is EventServiceHit => h !== null) ?? null;
    }
    if (!hit) return null;

    // Preferred: the content shell's canonical navigation command (the exact deeplink the app uses).
    // Send the user's real `lang` (not a hardcode); the `navigation_command.options.url` we read is a
    // pure uuid deeplink — verified byte-identical across locales — so the result is locale-invariant.
    if (hit.contentUuid) {
      try {
        const contentPayload = await apiRequest<ContentResponseBody, void, void>({
          url: `${apiUrl}/content/tenant/v3/buildings/${encodeURIComponent(buildingUuid)}/content/${encodeURIComponent(hit.contentUuid)}?lang=${encodeURIComponent(locale)}`,
          method: HttpMethod.GET,
          headers,
        });
        const navUrl = !contentPayload.isEmpty()
          ? contentPayload.data.data?.content?.navigation_command?.options?.url
          : undefined;
        if (navUrl) return navUrl;
      } catch {
        // fall through to the built url below
      }
    }

    // Fallback: build the building-prefixed event route from the resolved uuids.
    const base = (sbBaseUrl as string).replace(/\/+$/, '');
    return `${base}/building/${encodeURIComponent(buildingUuid)}/providers/${encodeURIComponent(hit.companyUuid)}/events/${encodeURIComponent(hit.serviceUuid)}`;
  };

  const { data: url = null } = useQuery<string | null, Error>({
    queryKey: ['event-deeplink', buildingUuid, serviceId],
    queryFn: resolveEventUrl,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const open = useCallback((): void => {
    if (!url) return;
    if (sdk?.redirectToContent) {
      sdk.redirectToContent({ value: '', type: LinkTypeEnum.WEBVIEW, options: { url } });
    } else {
      window.open(url, '_self');
    }
  }, [url, sdk]);

  return { url, open };
};
